/**
 * App router.
 *
 * Uses preact-iso for lightweight client-side routing (~3 KB). Screens are
 * currently statically imported; lazy-imports can be introduced once the
 * stage-code-split budget becomes interesting.
 *
 * On boot we read the session via loadSession(). Based on the resulting
 * state the Welcome screen dispatches the child to the correct onboarding
 * flow (new → family setup, family-only → child picker, active → map).
 */

import { useEffect } from 'preact/hooks';
import { LocationProvider, Router, Route, useLocation } from 'preact-iso';
import { Welcome } from './screens/Welcome';
import { WorldMap } from './screens/Map';
import { FamilySetup } from './screens/FamilySetup';
import { ChildPicker } from './screens/ChildPicker';
import { Island } from './screens/Island';
import { StageMeet } from './screens/StageMeet';
import { StageDraw } from './screens/StageDraw';
import { StageHear } from './screens/StageHear';
import { StageSay } from './screens/StageSay';
import { StageGarden } from './screens/StageGarden';
import { CollectionBook } from './screens/CollectionBook';
import { ParentDash } from './screens/ParentDash';
import { Privacy } from './screens/Privacy';
import { DevImages } from './screens/DevImages';
import { OfflineBanner } from './components/OfflineBanner';
import { installAutoUnlock } from './state/audio';
import { loadSession } from './state/user';

export function App() {
  useEffect(() => {
    void loadSession();
    // Rescue deep-linked entries + reloads: whatever the first tap is,
    // unlock the AudioContext so subsequent playback works.
    installAutoUnlock();
  }, []);

  return (
    <LocationProvider>
      <OfflineBanner />
      <main class="min-h-dvh flex flex-col">
        <RouteFrame />
      </main>
    </LocationProvider>
  );
}

/**
 * Wraps the Router in a div whose `key` changes with every navigation,
 * causing Preact to remount the subtree on each route change and
 * re-trigger the `.route-enter` CSS animation (260 ms fade+slide).
 */
function RouteFrame() {
  const loc = useLocation();
  return (
    <div key={loc.path} class="flex-1 flex flex-col route-enter">
      <Router>
        <Route path="/" component={Welcome} />
        <Route path="/setup" component={FamilySetup} />
        <Route path="/pick" component={ChildPicker} />
        <Route path="/map" component={WorldMap} />
        <Route path="/island/:id" component={Island} />
        <Route path="/island/:id/meet" component={StageMeet} />
        <Route path="/island/:id/draw" component={StageDraw} />
        <Route path="/island/:id/hear" component={StageHear} />
        <Route path="/island/:id/say" component={StageSay} />
        <Route path="/island/:id/garden" component={StageGarden} />
        <Route path="/collection" component={CollectionBook} />
        <Route path="/parent" component={ParentDash} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/_dev/images" component={DevImages} />
        <Route default component={NotFound} />
      </Router>
    </div>
  );
}

function NotFound() {
  return (
    <div class="flex-1 flex items-center justify-center p-6 text-center">
      <div>
        <p class="text-5xl mb-4">🗺️</p>
        <p class="text-2xl font-display mb-2">لَمْ أَجِدْ هٰذِهِ الصَّفْحَةَ</p>
        <p class="text-sm opacity-70" dir="ltr">Page not found</p>
        <a
          href="/"
          class="mt-6 inline-block px-6 py-3 rounded-2xl bg-coral text-white font-bold"
        >
          العَوْدَةُ إِلَى البَيْتِ
        </a>
      </div>
    </div>
  );
}
